import { useTranslation } from 'react-i18next';
import {
  ErrorModal,
  PageContainer,
  PageFooter,
  PageHeader,
} from '../../../shared_components';
import { useKeycloakStore, useUserDataStore } from '../../../shared_stores';
import { observer } from 'mobx-react-lite';
import {
  Button,
  Flex,
  Stack,
  Image,
  TextInput,
  Space,
  FileButton,
  Modal,
  Text,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import classes from '../../../app.module.css';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { userInfo } from '@web/common';
import { z } from 'zod';

export const Profile = observer(() => {
  const { t } = useTranslation(['common', 'profile']);
  const navigate = useNavigate();
  const kcStore = useKeycloakStore();
  const userDataStore = useUserDataStore();
  const [opened, { open, close }] = useDisclosure(false);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [profilePicture, setProfilePicture] = useState<Blob | null>(null);
  const [displayError, setDisplayError] = useState(false);
  const form = useForm<z.input<typeof userInfo>>({
    initialValues: userDataStore.userInfo,
    validate: zodResolver(z.object({ nickname: z.string() })),
  });

  const handleChange = () => {
    if (form.isDirty()) {
      setShowSaveButton(true);
    }
  };

  const handleSubmit = async () => {
    try {
      await userDataStore.updateUserInfo(kcStore.token, form.getValues());
      if (profilePicture) {
        await userDataStore.updateProfilePicture(kcStore.token, profilePicture);
      }
    } catch (error) {
      console.error(error);
      setDisplayError(true);
    } finally {
      setShowSaveButton(false);
    }
  };

  const handleAccountDeletion = async () => {
    try {
      await kcStore.deleteUser();
    } catch (error) {
      console.error(error);
      setDisplayError(true);
    } finally {
      close();
    }
  };

  return (
    <PageContainer>
      <ErrorModal
        opened={displayError}
        onClose={() => setDisplayError(false)}
      />
      <Modal
        classNames={{ content: classes.modalContentWhite }}
        opened={opened}
        onClose={close}
        title={t('profile:delete_account_title')}
      >
        <Stack>
          <Text>{t('profile:delete_account_message')}</Text>
          <Button
            onClick={() => void handleAccountDeletion()}
            style={{ backgroundColor: 'var(--mantine-color-otherRed-0)' }}
          >
            {t('delete_account')}
          </Button>
          <Button variant={'outline'} onClick={close}>
            {t('cancel')}
          </Button>
        </Stack>
      </Modal>
      <PageHeader title={t('profile:profile_title')} />
      <Stack gap={0}>
        <form
          onChange={handleChange}
          onSubmit={form.onSubmit(handleSubmit, console.log)}
        >
          <Flex direction={'row'} justify={'center'}>
            <FileButton
              accept="image/png, image/jpeg"
              onChange={(value) => {
                setProfilePicture(value);
                setShowSaveButton(true);
              }}
            >
              {(props) => (
                <Button
                  {...props}
                  variant={'text'}
                  style={{ height: '56px' }}
                  leftSection={
                    <div style={{ marginRight: 'var(--mantine-spacing-xs)' }}>
                      <Image
                        style={{
                          height: '56px',
                          aspectRatio: '1/1',
                          borderRadius: '50%',
                        }}
                        src={
                          profilePicture
                            ? URL.createObjectURL(profilePicture)
                            : userDataStore.profilePicture
                              ? `data:image/*;base64,${userDataStore.profilePicture}`
                              : '/../../../assets/img/profile_black.svg'
                        }
                      />
                      <EditOutlinedIcon
                        style={{
                          fontSize: 28,
                          padding: 4,
                          fill: 'var(--mantine-color-clinicalWhite-0)',
                          backgroundColor: 'var(--mantine-color-otherGrey60-0)',
                          borderRadius: '50%',
                          position: 'absolute',
                          bottom: 0,
                          right: 'var(--mantine-spacing-xs)',
                        }}
                      />
                    </div>
                  }
                />
              )}
            </FileButton>
            <TextInput
              placeholder={t('name')}
              key={form.key('nickName')}
              style={{ flexGrow: 1 }}
              {...form.getInputProps('nickName')}
            />
          </Flex>
          <Space h={'var(--mantine-spacing-md)'} />
          {showSaveButton && (
            <Button size="md" fullWidth onClick={() => void handleSubmit()}>
              {t('common:save_changes')}
            </Button>
          )}
        </form>
        <Space h={'var(--mantine-spacing-lg)'} />

        <Button
          variant="list"
          onClick={() => void navigate('/profile/health-data')}
          classNames={{
            inner: classes.flexStartButtonInner,
            label: classes.flexGrowButtonLabel,
          }}
          leftSection={<PersonOutlineOutlinedIcon />}
          rightSection={
            <EditOutlinedIcon
              style={{
                fontSize: 20,
                fill: 'var(--mantine-primary-color-filled)',
              }}
            />
          }
        >
          {t('common:contact_data_title')}
        </Button>
        <Space
          h={0}
          style={{
            borderBottom: '1px solid var(--mantine-color-otherGrey10-0)',
          }}
        />
        <Button
          variant="list"
          onClick={() => void navigate('/profile/consents')}
          classNames={{
            inner: classes.flexStartButtonInner,
            label: classes.flexGrowButtonLabel,
          }}
          leftSection={<CheckBoxOutlinedIcon />}
          rightSection={
            <EditOutlinedIcon
              style={{
                fontSize: 20,
                fill: 'var(--mantine-primary-color-filled)',
              }}
            />
          }
        >
          {t('profile:edit_consents_title')}
        </Button>
        <Space
          h={0}
          style={{
            borderBottom: '1px solid var(--mantine-color-otherGrey10-0)',
          }}
        />
        <Button
          variant="list"
          onClick={() => void navigate('/profile/invoices')}
          classNames={{
            inner: classes.flexStartButtonInner,
            label: classes.flexGrowButtonLabel,
          }}
          leftSection={<DescriptionOutlinedIcon />}
          rightSection={<ChevronRightIcon />}
        >
          {t('profile:view_invoices_title')}
        </Button>
        <Space
          h={0}
          style={{
            borderBottom: '1px solid var(--mantine-color-otherGrey10-0)',
          }}
        />
        <Button
          variant="list"
          onClick={() => void navigate('/reports/dashboard')}
          classNames={{
            inner: classes.flexStartButtonInner,
            label: classes.flexGrowButtonLabel,
          }}
          leftSection={<QueryStatsOutlinedIcon />}
          rightSection={<ChevronRightIcon />}
        >
          {t('profile:to_reports')}
        </Button>

        <Space h={'var(--mantine-spacing-xl)'} />

        <Button variant="outline" onClick={() => void kcStore.logout()}>
          {t('logout')}
        </Button>
        <Button
          variant="text"
          style={{ color: 'var(--mantine-color-otherRed-0) !important' }}
          onClick={open}
        >
          {t('common:delete_account')}
        </Button>
      </Stack>
      <PageFooter leftOnClick={() => window.history.back()} hideRight />
    </PageContainer>
  );
});
